import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { listCoupons, deleteCoupon, toggleCouponStatus, searchCouponsByCode, updateCoupon } from "../services/couponService";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	Typography,
	Alert,
	IconButton,
	TextField,
	Switch,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

const CouponList = () => {
	const { user } = useAuth();
	const [coupons, setCoupons] = useState([]);
	const [filteredCoupons, setFilteredCoupons] = useState([]);
	const [message, setMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [currentCoupon, setCurrentCoupon] = useState(null);
	const [editedCoupon, setEditedCoupon] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchCoupons = async () => {
			try {
				const couponList = await listCoupons();
				setCoupons(couponList);
				setFilteredCoupons(couponList);
				setLoading(false);
			} catch (error) {
				setMessage(error.message);
				setLoading(false);
			}
		};

		fetchCoupons();
	}, []);

	useEffect(() => {
		if (searchTerm) {
			const search = async () => {
				try {
					const results = await searchCouponsByCode(searchTerm);
					setFilteredCoupons(results);
				} catch (error) {
					setMessage(error.message);
				}
			};
			search();
		} else {
			setFilteredCoupons(coupons);
		}
	}, [searchTerm, coupons]);

	const handleToggleStatus = async (couponId, active) => {
		try {
			await toggleCouponStatus(couponId, active);
			setCoupons(coupons.map((coupon) => (coupon.id === couponId ? { ...coupon, active } : coupon)));
			setMessage(`Coupon ${active ? "enabled" : "disabled"} successfully!`);
		} catch (error) {
			setMessage(error.message);
		}
	};

	const handleEditClick = (coupon) => {
		setCurrentCoupon(coupon);
		setEditedCoupon({ ...coupon }); // Create a copy for editing
		setEditDialogOpen(true);
	};

	const handleEditChange = (e) => {
		const { name, value } = e.target;
		setEditedCoupon((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSaveEdit = async () => {
		if (!editedCoupon) return;

		// Validation
		const errors = {};
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (new Date(editedCoupon.expiryDate) < today) {
			errors.expiryDate = "Expiry date must be in the future";
		}

		if (parseFloat(editedCoupon.discount) <= 0) {
			errors.discount = "Discount must be positive";
		}

		if (parseInt(editedCoupon.maxUses) <= 0) {
			errors.maxUses = "Max uses must be positive";
		}

		if (parseInt(editedCoupon.userMaxUses) <= 0) {
			errors.userMaxUses = "Max uses per user must be positive";
		}

		if (editedCoupon.discountType === "percentage" && parseFloat(editedCoupon.discount) > 100) {
			errors.discount = "Percentage discount cannot exceed 100%";
		}

		if (editedCoupon.maxUses && editedCoupon.userMaxUses && parseInt(editedCoupon.userMaxUses) > parseInt(editedCoupon.maxUses)) {
			errors.userMaxUses = "Per-user limit cannot exceed total uses";
		}

		if (Object.keys(errors).length > 0) {
			setErrorMessage(Object.values(errors).join(", "));
			return;
		}

		try {
			// Prepare update data (only include fields that can be edited)
			const updateData = {
				discount: parseFloat(editedCoupon.discount),
				discountType: editedCoupon.discountType,
				expiryDate: new Date(editedCoupon.expiryDate).toISOString(),
				maxUses: parseInt(editedCoupon.maxUses),
				userMaxUses: parseInt(editedCoupon.userMaxUses),
			};

			await updateCoupon(editedCoupon.id, updateData);

			// Update local state
			setCoupons(coupons.map((coupon) => (coupon.id === editedCoupon.id ? { ...coupon, ...updateData } : coupon)));

			setMessage("Coupon updated successfully!");
			handleEditClose();
		} catch (error) {
			setMessage(`Failed to update coupon: ${error.message}`);
		}
	};

	const handleEditClose = () => {
		setEditDialogOpen(false);
		setCurrentCoupon(null);
		setEditedCoupon(null);
	};

	const handleDelete = async (couponId) => {
		try {
			await deleteCoupon(couponId);
			setCoupons(coupons.filter((coupon) => coupon.id !== couponId));
			setMessage("Coupon deleted successfully!");
		} catch (error) {
			setMessage(error.message);
		}
	};
	useEffect(() => {
		if (user) {
			navigate("/");
		} else {
			navigate("/phone-auth");
		}
	}, [user]);

	if (!user) {
		return (
			<Typography variant="h6" color="error">
				Please sign in to view coupons
			</Typography>
		);
	}

	if (loading) {
		return <Typography>Loading coupons...</Typography>;
	}

	return (
		<Paper elevation={3} sx={{ p: 1, mt: 2 }}>
			<Typography variant="h5" gutterBottom align="center">
				Coupon List
			</Typography>
			{message && (
				<Alert severity={message.includes("error") ? "error" : "success"} sx={{ mb: 2 }}>
					{message}
				</Alert>
			)}
			<TextField fullWidth label="Search by Coupon Code" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }} />
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Coupon Code</TableCell>
							<TableCell>Discount</TableCell>
							<TableCell>Discount Type</TableCell>
							<TableCell>Expiry Date</TableCell>
							<TableCell>Max Uses</TableCell>
							<TableCell>User Max Uses</TableCell>
							<TableCell>Use Count</TableCell>
							<TableCell>Active</TableCell>
							<TableCell>users</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredCoupons.map((coupon) => (
							<TableRow key={coupon.id}>
								<TableCell>{coupon.code}</TableCell>
								<TableCell>
									{coupon.discount}
									{coupon.discountType === "percentage" ? "%" : ""}
								</TableCell>
								<TableCell>{coupon.discountType}</TableCell>
								<TableCell>{new Date(coupon.expiryDate).toLocaleDateString()}</TableCell>
								<TableCell>{coupon.maxUses}</TableCell>
								<TableCell>{coupon.userMaxUses}</TableCell>
								<TableCell>{coupon.useCount}</TableCell>
								<TableCell>
									<Switch checked={coupon.active} onChange={(e) => handleToggleStatus(coupon.id, e.target.checked)} />
								</TableCell>
								<TableCell>
									{coupon.userUses &&
										Object.entries(coupon.userUses).map(([phone, count]) => (
											<div key={phone}>
												{phone}: {count}
											</div>
										))}
								</TableCell>
								<TableCell>
									<IconButton onClick={() => handleEditClick(coupon)}>
										<EditIcon />
									</IconButton>
									{/* <IconButton onClick={() => handleDelete(coupon.id)}>
										<DeleteIcon />
									</IconButton> */}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Edit Dialog */}
			<Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="lg">
				<DialogTitle>Edit Coupon</DialogTitle>
				<DialogContent>
					{editedCoupon && (
						<Grid container spacing={2} sx={{ pt: 1 }}>
							<Grid item xs={12}>
								<TextField fullWidth label="Coupon Code" value={editedCoupon.code} disabled />
							</Grid>
							<Grid item xs={12}>
								<TextField fullWidth label="Discount" type="number" value={editedCoupon.discount} name="discount" onChange={handleEditChange} />
							</Grid>
							<Grid item xs={12}>
								<FormControl fullWidth>
									<InputLabel>Discount Type</InputLabel>
									<Select value={editedCoupon.discountType} label="Discount Type" name="discountType" onChange={handleEditChange}>
										<MenuItem value="percentage">Percentage</MenuItem>
										<MenuItem value="flat">Flat</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Expiry Date"
									type="date"
									value={new Date(editedCoupon.expiryDate).toISOString().split("T")[0]}
									InputLabelProps={{ shrink: true }}
									name="expiryDate"
									onChange={handleEditChange}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField fullWidth label="Max Uses" type="number" value={editedCoupon.maxUses} name="maxUses" onChange={handleEditChange} />
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Max Uses Per User"
									type="number"
									value={editedCoupon.userMaxUses}
									name="userMaxUses"
									onChange={handleEditChange}
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					{errorMessage && <Alert severity="error">{errorMessage}</Alert>}
					<Button onClick={handleEditClose}>Cancel</Button>
					<Button onClick={handleSaveEdit}>Save</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};

export default CouponList;
